import { ENTRY_DATA_SECTION_KEYS } from "@frontend/common";

import { ArticleEntryData } from "@/types/articleEntryData.types";
import { DealLaScSection } from "@/types/dealLaSc.types";
import {
  DEFAULT_FOCUS_STATUS,
  useTableCreatorFocusStatus,
} from "@FillInTableContainer/shared/context/TableCreatorFocusStatusContext";
import { useTableCreatorCurrentHistory } from "@FillInTableContainer/shared/context/TableCreatorHistoryContext";
import {
  DEFAULT_POSITION_INFO,
  useTableCreatorPositionInfo,
} from "@FillInTableContainer/shared/context/TableCreatorPositionInfoContext";
import { useEntryDataState } from "@stores/entryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

interface Props {
  groupTableKey: DealLaScSection;
  groupTableIndex: number;
  groupShellSectionKey: string | null;
  groupShellSectionIndex: number | null;
  tableKey: string | null;
  tableIndex: number;
}

const useDealTermsFinancialTermsGroupShellSection = ({
  groupShellSectionIndex,
  groupShellSectionKey,
  groupTableIndex,
  groupTableKey,
  tableIndex,
  tableKey,
}: Props) => {
  const [entryData, setEntryData] = useEntryDataState(getPressIdFromURL());
  const { focusStatus, changeFocusStatus } = useTableCreatorFocusStatus();
  const { changeCurrentHistory } = useTableCreatorCurrentHistory();

  const { info, changeInfo } = useTableCreatorPositionInfo();

  const handleClearGroupShellSection = () => {
    const clearFinancialTermsGroupShell = (): ArticleEntryData => {
      if (info.groupShellSectionIndex === null) return entryData;
      if (info.groupShellSectionKey !== "Main Financial Terms Table")
        return entryData;

      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Main Financial Terms Table": entryData[
            ENTRY_DATA_SECTION_KEYS.dealsLaSc
          ]?.["Main Financial Terms Table"]?.map(
            (tableInfo, tableInfoIndex) => {
              if (tableInfoIndex === info.tableIndex) {
                return {
                  ...tableInfo,
                  "Disclosed Payment Sum(LA&SC)": {
                    ...tableInfo["Disclosed Payment Sum(LA&SC)"],
                    value: [],
                  },
                  "Milestone Payment And Detail": [
                    {
                      ...tableInfo["Milestone Payment And Detail"]?.[0],
                      "Milestone Payment Detail(LA&SC)": {
                        ...tableInfo["Milestone Payment And Detail"]?.[0][
                          "Milestone Payment Detail(LA&SC)"
                        ],
                        value: [],
                      },
                      "Milestone Payment(LA&SC)": {
                        ...tableInfo["Milestone Payment And Detail"]?.[0][
                          "Milestone Payment(LA&SC)"
                        ],
                        value: [],
                      },
                    },
                  ],
                  "Other Payment And Detail": [
                    {
                      ...tableInfo["Other Payment And Detail"]?.[0],
                      "Other Payment Detail(LA&SC)": {
                        ...tableInfo["Other Payment And Detail"]?.[0][
                          "Other Payment Detail(LA&SC)"
                        ],
                        value: [],
                      },
                      "Other Payment(LA&SC)": {
                        ...tableInfo["Other Payment And Detail"]?.[0][
                          "Other Payment(LA&SC)"
                        ],
                        value: [],
                      },
                    },
                  ],
                  "Paying Partner Axis(LA&SC)": {
                    ...tableInfo["Paying Partner Axis(LA&SC)"],
                    value: [],
                  },
                  "Receiving Partner Axis(LA&SC)": {
                    ...tableInfo["Receiving Partner Axis(LA&SC)"],
                    value: [],
                  },
                  "Royalty(LA&SC)": {
                    ...tableInfo["Royalty(LA&SC)"],
                    value: [],
                  },
                  "Total Payment(LA&SC)": {
                    ...tableInfo["Total Payment(LA&SC)"],
                    value: [],
                  },
                  "Upfront Payment(LA&SC)": {
                    ...tableInfo["Upfront Payment(LA&SC)"],
                    value: [],
                  },
                };
              }

              return tableInfo;
            },
          ),
        },
      };
    };

    if (info.groupShellSectionKey === "Main Financial Terms Table") {
      const financialTermsTableLength =
        entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.[
          "Main Financial Terms Table"
        ]?.length || 0;

      setEntryData(clearFinancialTermsGroupShell());
      changeCurrentHistory({
        entryData: clearFinancialTermsGroupShell(),
        changedDataInfo: {
          target: "groupShellSection",
          action: "clear",
          position: {
            groupTableIndex,
            groupTableKey,
            tableKey,
            tableIndex,
            groupShellSectionKey,
            groupShellSectionIndex,
            groupShellIndex: null,
            groupShellKey: null,
            subGroupShellIndex: null,
            subGroupShellKey: null,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
      if (financialTermsTableLength > 1) {
        changeFocusStatus({ type: "delete", container: "groupShellSection" });
      } else {
        changeFocusStatus(DEFAULT_FOCUS_STATUS);
      }
    }
  };

  const handleDeleteGroupShellSection = () => {
    if (info.groupShellSectionKey !== "Main Financial Terms Table") return;

    const deleteFinancialTermsGroupShell = (): ArticleEntryData => {
      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Main Financial Terms Table": entryData[
            ENTRY_DATA_SECTION_KEYS.dealsLaSc
          ]?.["Main Financial Terms Table"]?.filter((_, tableInfoIndex) => {
            if (tableInfoIndex === info.tableIndex) return false;
            return true;
          }),
        },
      };
    };

    if (info.groupShellSectionKey === "Main Financial Terms Table") {
      setEntryData(deleteFinancialTermsGroupShell());
      changeCurrentHistory({
        entryData: deleteFinancialTermsGroupShell(),
        changedDataInfo: {
          target: "groupShellSection",
          action: "delete",
          position: {
            groupTableIndex,
            groupTableKey,
            tableKey,
            tableIndex,
            groupShellSectionKey,
            groupShellSectionIndex,
            groupShellIndex: null,
            groupShellKey: null,
            subGroupShellIndex: null,
            subGroupShellKey: null,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
      changeFocusStatus(DEFAULT_FOCUS_STATUS);
      changeInfo(DEFAULT_POSITION_INFO);
    }
  };

  const handleClickToClearFinancialTermsTableGroupShellSection = () => {
    if (focusStatus.type === "clear") {
      handleClearGroupShellSection();
    }

    if (focusStatus.type === "delete") {
      handleDeleteGroupShellSection();
    }
  };

  return { handleClickToClearFinancialTermsTableGroupShellSection };
};

export default useDealTermsFinancialTermsGroupShellSection;
